import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, Spin, message } from 'antd';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AcademiesPage from './pages/AcademiesPage';
import NotFoundPage from './pages/NotFoundPage';
import { userService } from './services';
import './App.css';

const AcademyCatalogPage = lazy(() => import('./pages/AcademyCatalogPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ConsultingTopicsPage = lazy(() => import('./pages/ConsultingTopicsPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const PurchasesPage = lazy(() => import('./pages/PurchasesPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LearningPage = lazy(() => import('./pages/LearningPage'));
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'));
const StudioPage = lazy(() => import('./pages/StudioPage'));
const AdminPanelPage = lazy(() => import('./pages/AdminPanelPage'));
const LegalDocumentsPage = lazy(() => import('./pages/LegalDocumentsPage'));

const isMaintenanceModeEnabled = () => String(process.env.REACT_APP_MAINTENANCE || '').toLowerCase() === 'true';

const MaintenancePage = () => (
  <div className="maintenance-shell">
    <div className="maintenance-card">
      <span className="maintenance-kicker">Syntera</span>
      <h1>Yakında açılıyoruz</h1>
      <p>
        Akademi platformumuz için son kontroller yapılıyor.
        Kısa süre sonra yeniden erişime açılacak.
      </p>
    </div>
  </div>
);

const SessionActivityTracker = () => {
  const location = useLocation();
  const lastWarningKeyRef = useRef('');
  const lastWarningAtRef = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return undefined;
    }

    const payload = {
      path: location.pathname,
      course_slug: location.pathname.startsWith('/learn/')
        ? location.pathname.replace('/learn/', '').split('/')[0]
        : '',
    };

    const sendHeartbeat = async () => {
      try {
        const response = await userService.sessionHeartbeat(payload);
        const warningKey = response.data?.warning_key;
        const warningMessage = response.data?.warning_message;

        if (!response.data?.sharing_suspected || !warningKey || !warningMessage) {
          lastWarningKeyRef.current = '';
          return;
        }

        const now = Date.now();
        const shouldNotify =
          warningKey !== lastWarningKeyRef.current ||
          now - lastWarningAtRef.current >= 2 * 60 * 1000;

        if (shouldNotify) {
          message.warning({
            content: warningMessage,
            duration: 5,
            key: warningKey,
          });
          lastWarningKeyRef.current = warningKey;
          lastWarningAtRef.current = now;
        }
      } catch (_error) {
        // best effort activity signal
      }
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [location.pathname]);

  return null;
};

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('access_token');
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const GuestOnlyRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('access_token');
  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

const StaffRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('access_token');
  const [status, setStatus] = useState(() => {
    if (!isAuthenticated) {
      return 'unauthenticated';
    }

    return 'checking';
  });

  useEffect(() => {
    if (!isAuthenticated || status !== 'checking') {
      return;
    }

    let active = true;

    const verifyRole = async () => {
      try {
        const response = await userService.getProfile();
        const nextIsStaff = !!response.data.is_staff;
        localStorage.setItem('is_staff', String(nextIsStaff));
        if (active) {
          setStatus(nextIsStaff ? 'authorized' : 'forbidden');
        }
      } catch (error) {
        localStorage.removeItem('is_staff');
        if (active) {
          setStatus('forbidden');
        }
      }
    };

    verifyRole();

    return () => {
      active = false;
    };
  }, [isAuthenticated, status]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (status === 'checking') {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><Spin size="large" /></div>;
  }

  return status === 'authorized' ? children : <Navigate to="/" replace />;
};

const App = () => {
  if (isMaintenanceModeEnabled()) {
    return <MaintenancePage />;
  }

  return (
    <Router>
      <ScrollToTop />
      <SessionActivityTracker />
      <Layout className="app-shell">
        <Header />
        <Layout.Content className="app-content">
          <Suspense fallback={<div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><Spin size="large" /></div>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/egitimlerimiz" element={<AcademiesPage />} />
              <Route path="/egitimlerimiz/:groupSlug" element={<AcademiesPage />} />
              <Route path="/akademilerimiz" element={<AcademyCatalogPage />} />
              <Route path="/danismanlik-konularimiz" element={<ConsultingTopicsPage />} />
              <Route path="/hakkimizda" element={<AboutPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/iletisim" element={<ContactPage />} />
              <Route path="/login" element={<GuestOnlyRoute><LoginPage /></GuestOnlyRoute>} />
              <Route path="/register" element={<GuestOnlyRoute><RegisterPage /></GuestOnlyRoute>} />
              <Route path="/yasal" element={<LegalDocumentsPage />} />
              <Route path="/yasal/:slug" element={<LegalDocumentsPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/course/:slug" element={<CourseDetailPage />} />
              <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
              <Route path="/purchases" element={<ProtectedRoute><PurchasesPage /></ProtectedRoute>} />
              <Route path="/egitimlerim" element={<ProtectedRoute><PurchasesPage /></ProtectedRoute>} />
              <Route path="/documents/:slug" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
              <Route path="/dokumanlar/:slug" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
              <Route path="/siparislerim" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/profil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/learn/:slug" element={<ProtectedRoute><LearningPage /></ProtectedRoute>} />
              <Route path="/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />
              <Route path="/sertifikalarim" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />
              <Route path="/studio" element={<StaffRoute><StudioPage /></StaffRoute>} />
              <Route path="/egitmen-studyosu" element={<StaffRoute><StudioPage /></StaffRoute>} />
              <Route path="/admin-panel" element={<StaffRoute><AdminPanelPage /></StaffRoute>} />
              <Route path="/yonetim" element={<StaffRoute><AdminPanelPage /></StaffRoute>} />
              <Route path="/yonetim-paneli" element={<StaffRoute><AdminPanelPage /></StaffRoute>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout.Content>
        <Footer />
      </Layout>
    </Router>
  );
};

export default App;
